<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { NIcon } from 'naive-ui';
import { IconChatActive, IconDocumentText, IconHelp } from '@/components/design/icons';
import { useLocalStorage } from '@vueuse/core';
import WorkspaceLayout from '@/components/design/layout/WorkspaceLayout.vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaDrawer from '@/components/design/base/GaiaDrawer.vue';
import GaiaPageHeader from '@/components/design/composite/GaiaPageHeader.vue';
import GaiaSpinner from '@/components/design/base/GaiaSpinner.vue';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import ReviewAnalysis from '@/components/reviewProject/ReviewAnalysis.vue';
import ReviewDataRoom from '@/components/reviewProject/ReviewDataRoom.vue';
import ReviewFileNotes from '@/components/reviewProject/ReviewFileNotes.vue';
import ReviewFilePreview from '@/components/reviewProject/ReviewFilePreview.vue';
import ReviewProjectBreadcrumb from '@/components/reviewProject/ReviewProjectBreadcrumb.vue';
import ReviewProjectChatPanel from '@/components/reviewProject/ReviewProjectChatPanel.vue';
import ReviewProjectPageMenu from '@/components/reviewProject/ReviewProjectPageMenu.vue';
import ReviewProjectQuestions from '@/components/reviewProject/ReviewProjectQuestions.vue';
import ReviewReport from '@/components/reviewProject/ReviewReport.vue';
import { useReviewProject } from '@/composables/useReviewProject';
import { useReviewProjectWebSocket } from '@/composables/useReviewProjectWebSocket';
import { ReviewProjectService } from '@/services/reviewProjectService';
import { useReviewProjectStore } from '@/stores/reviewProjectStore';
import { logger } from '@/utils/logger';

const route = useRoute();

const projectUid = computed(() => route.params.uid as string);
const { project, loading, error, fetchProject } = useReviewProject(() => projectUid.value);

const reviewProjectStore = useReviewProjectStore();

// Which of the workspace's optional surfaces are showing. The data room's
// collapsed state and width persist — both are working preferences, not view
// state — while the report and the chat are opened per visit from the title row.
const dataRoomCollapsed = useLocalStorage<boolean>('review-project-data-room-collapsed', false);
// Safe to store straight through: the shell paints a live drag itself and only
// reports the settled width, so this is written once per drag, not per frame.
const dataRoomWidth = useLocalStorage<number>('review-project-data-room-width', 344);

const chatOpen = ref<boolean>(false);
const chatExpanded = ref<boolean>(false);

function closeChat(): void {
  chatOpen.value = false;
  // Reopening should give back the rail, not last visit's full-width state.
  chatExpanded.value = false;
}

/**
 * The panes that open under the analysis grid, from the title row. Both are
 * about the review's output rather than its data — the report the team writes
 * and the questions it is still carrying — so they share one mechanism: opened
 * from a toggle, closed from their own header, and laid out side by side in one
 * band whose share of the column they also share.
 */
type StackedPane = 'report' | 'qa';

const openPanes = ref<StackedPane[]>([]);

/**
 * The band has taken the whole right column, so the analysis grid steps aside.
 * Expanding belongs to the band rather than to a pane: with both open, a pane
 * claiming the column for itself would have to evict its neighbour, and the
 * point of expanding is more room to work in — not fewer panes.
 */
const stackExpanded = ref<boolean>(false);

const stackedPanes = computed(() => openPanes.value);

function isOpen(pane: StackedPane): boolean {
  return openPanes.value.includes(pane);
}

function closePane(pane: StackedPane): void {
  openPanes.value = openPanes.value.filter(open => open !== pane);
  // Nothing left to expand — and leaving it set would hide the analysis behind
  // an empty column.
  if (!openPanes.value.length) stackExpanded.value = false;
}

function togglePane(pane: StackedPane): void {
  if (isOpen(pane)) {
    closePane(pane);
  } else {
    openPanes.value = [...openPanes.value, pane];
  }
}

const analysisVisible = computed(() => !stackExpanded.value);

// The mock's 55/45 split between the grid and the band under it.
const analysisStyle = computed(() =>
  openPanes.value.length
    ? { flex: '1 1 55%', minHeight: '360px' }
    : { flex: '1 1 auto', minHeight: '0' }
);

const stackStyle = computed(() =>
  stackExpanded.value
    ? { flex: '1 1 auto', minHeight: '0' }
    : { flex: '1 1 45%', minHeight: '300px' }
);

/**
 * Each pane in the band takes half the width but refuses to go under 340px, so
 * the row wraps to stacked instead of shrinking into two unusable slivers. That
 * is what makes the chat safe to open: it narrows this column, and on a 1440
 * screen with the data room open there is not room for two panes side by side.
 * The min-height is what lets a wrapped second line have somewhere to go — the
 * band grows past its share and the column scrolls.
 */
const stackPaneStyle = { flex: '1 1 340px', minWidth: '0', minHeight: '300px' } as const;

/**
 * The band's own top rule comes from its panes — dropped once it fills the
 * column and has nothing above it — and the left one of a pair also rules off
 * against its neighbour. Insertion order decides which that is, so the pane you
 * opened first stays on the left.
 */
function dividerFor(pane: StackedPane): string {
  const edges: string[] = [];
  if (!stackExpanded.value) edges.push('top');
  if (stackedPanes.value.length === 2 && stackedPanes.value[0] === pane) edges.push('right');
  return edges.length ? edges.join(' ') : 'none';
}

onMounted(() => {
  // Selection/preview state from a previously opened project must not leak in.
  reviewProjectStore.reset();
  void fetchProject();
});

// Leaving the view (route change) with a pending debounced save: persist now.
onBeforeUnmount(() => {
  reviewProjectStore.flushNotes();
  reviewProjectStore.flushReport();
  // The report-generation status poll must not outlive the view.
  reviewProjectStore.stopTaskGroupPolling();
});

// Another client changed the analysis list; the events carry no analysis data,
// so refetch. Skip while null — the analysis pane's first mount fetches fresh.
async function refetchAnalyses(): Promise<void> {
  if (reviewProjectStore.analyses === null) return;
  try {
    reviewProjectStore.setAnalyses(await ReviewProjectService.listAnalyses(projectUid.value));
  } catch (error_) {
    logger.error('Failed to refresh analyses:', error_);
  }
}

useReviewProjectWebSocket({
  projectUid: () => projectUid.value,
  onAnalysesStale: () => void refetchAnalyses(),
  // Refetch + echo/conflict handling live in the store, serialized with saves;
  // while the report soft lock is engaged this also re-checks the run status.
  onReportStale: () => {
    reviewProjectStore.handleReportStale(projectUid.value);
  },
});

// Confirming a report_generation_confirm interrupt dispatches the background
// report agent server-side: soft-lock the report editor right away, and show
// the report so the lock notice is where the user can see it.
function handleChatInterruptDecision(decision: {
  interruptType: string | null;
  action: string;
}): void {
  if (decision.interruptType === 'report_generation_confirm' && decision.action === 'confirm') {
    reviewProjectStore.notifyReportGenerationStarted(projectUid.value);
    if (!isOpen('report')) togglePane('report');
  }
}

// Background tracking for the files list's "Seen" progress — no error toast.
function handlePageVisit(page: number): void {
  const revisionUid = reviewProjectStore.previewTarget?.revisionUid;
  if (!revisionUid) return;
  ReviewProjectService.markFilePageSeen(projectUid.value, revisionUid, page).catch(
    (error_: unknown) => {
      logger.error('Failed to mark file page seen', error_);
    }
  );
}

// The drawers are driven by the store's targets, so closing one clears the
// target rather than flipping a local flag.
function handlePreviewShow(show: boolean): void {
  if (!show) reviewProjectStore.closePreview();
}

function handleNotesShow(show: boolean): void {
  // closeNotes flushes a dirty draft before clearing the target.
  if (!show) reviewProjectStore.closeNotes();
}
</script>

<template>
  <workspace-layout
    v-model:rail-width="dataRoomWidth"
    :drawer-open="chatOpen"
    :drawer-width="360"
    :drawer-expanded="chatExpanded"
    rail-resizable
    :rail-collapsed="dataRoomCollapsed"
    rail-resize-label="Resize the data room"
  >
    <template #breadcrumb>
      <review-project-breadcrumb :project-uid="projectUid" :project-name="project?.name" />
    </template>

    <template #title>
      <gaia-page-header :title="project?.name ?? '…'">
        <template #actions>
          <gaia-button
            :disabled="!project"
            data-tour="rp.report-toggle"
            @click="togglePane('report')"
          >
            <template #icon>
              <n-icon :component="IconDocumentText" />
            </template>
            Report
          </gaia-button>
          <gaia-button :disabled="!project" data-tour="rp.qa-toggle" @click="togglePane('qa')">
            <template #icon>
              <n-icon :component="IconHelp" />
            </template>
            Q&amp;A
          </gaia-button>
          <gaia-button
            :disabled="!project"
            data-tour="rp.chat-toggle"
            @click="chatOpen = !chatOpen"
          >
            <template #icon>
              <n-icon :component="IconChatActive" />
            </template>
            Chat
          </gaia-button>
          <review-project-page-menu :project-uid="projectUid" />
        </template>
      </gaia-page-header>

      <gaia-banner
        v-if="error"
        type="error"
        title="Couldn’t load the review project"
        :style="{ marginTop: '12px' }"
      >
        {{ error }}
      </gaia-banner>
    </template>

    <template v-if="project" #rail>
      <review-data-room
        v-model:collapsed="dataRoomCollapsed"
        :root-directory="project.data_room_root_directory"
        :project-uid="projectUid"
      />
    </template>

    <review-analysis
      v-if="project && analysisVisible"
      :project-uid="projectUid"
      :style="analysisStyle"
    />

    <!-- One band for both: side by side while there is room, wrapping to
         stacked when there is not. Expanding is the band's, so either pane's
         control grows both of them over the analysis. -->
    <n-flex v-if="project && stackedPanes.length" :size="0" :style="stackStyle">
      <review-report
        v-if="isOpen('report')"
        :project-uid="projectUid"
        :expanded="stackExpanded"
        :divider="dividerFor('report')"
        :style="stackPaneStyle"
        @update:expanded="value => (stackExpanded = value)"
        @close="closePane('report')"
      />

      <review-project-questions
        v-if="isOpen('qa')"
        :project-uid="projectUid"
        :expanded="stackExpanded"
        :divider="dividerFor('qa')"
        :style="stackPaneStyle"
        @update:expanded="value => (stackExpanded = value)"
        @close="closePane('qa')"
      />
    </n-flex>

    <gaia-spinner v-if="loading && !project" style="margin: 48px auto" />

    <template v-if="project" #drawer>
      <review-project-chat-panel
        v-model:expanded="chatExpanded"
        :project-uid="projectUid"
        :upload-directory-uid="project.data_room_root_directory.uid"
        :upload-directory-label="project.data_room_root_directory.directory_name"
        @close="closeChat"
        @interrupt-decision="handleChatInterruptDecision"
      />
    </template>
  </workspace-layout>

  <!-- The file preview and the file's notes open over the workspace rather than
       taking a track of it: both are about one file the user picked, and the
       mock's grid has no column for them. -->
  <gaia-drawer
    flush
    :show="!!reviewProjectStore.previewTarget"
    :width="720"
    title="File preview"
    @update:show="handlePreviewShow"
  >
    <review-file-preview
      :revision-uid="reviewProjectStore.previewTarget?.revisionUid"
      :page="reviewProjectStore.previewTarget?.page"
      :highlights="reviewProjectStore.previewTarget?.highlights"
      @page-visit="handlePageVisit"
    />
  </gaia-drawer>

  <gaia-drawer
    flush
    :show="!!reviewProjectStore.notesTarget"
    :width="460"
    title="Notes"
    :subtitle="reviewProjectStore.notesTarget?.fileName"
    @update:show="handleNotesShow"
  >
    <review-file-notes />
  </gaia-drawer>
</template>
